import { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import axios from "axios";
import axiosInstance from "../services/axiosInstance";
import { API_ENDPOINTS } from "../services/api/apiConfig";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Phone,
  ArrowRight,
  Shield,
  Heart,
  Stethoscope,
  Activity,
  UserCheck,
  Calendar,
  FileText,
} from "lucide-react";
import { fetchUserProfile } from "../services/userService";

interface LoginResponse {
  token: string;
  role: string;
  id: number;
  email: string;
  username?: string;
  fullName?: string;
}

const LoginPage = ({
  onLoginSuccess,
}: {
  onLoginSuccess: (token: string) => void;
}) => {
  const [formData, setFormData] = useState({ identifier: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState("");
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    localStorage.clear();
    sessionStorage.clear();

    try {

      const res = await axiosInstance.post<LoginResponse>(
        API_ENDPOINTS.AUTH.LOGIN,
        formData
      );

      const { token, role, id, email, username, fullName } = res.data;


      if (!id) {
        console.error("LoginPage: User ID is missing in login response");
        throw new Error("User ID not found in login response");
      }

      localStorage.setItem("token", token);
      localStorage.setItem("role", role.toLowerCase());
      localStorage.setItem("userId", id.toString());
      localStorage.setItem("userEmail", email);

      try {
        const userData = await fetchUserProfile(id.toString());

        localStorage.setItem(
          "userLabel",
          userData.fullName ||
          (userData.firstName && userData.lastName
            ? `${userData.firstName} ${userData.lastName}`
            : userData.username || "Utilisateur Inconnu")
        );
      } catch (profileError) {
        console.error("LoginPage: Failed to fetch user profile:", profileError);
        const fallbackName = fullName || username || "Utilisateur Inconnu";
        localStorage.setItem("userLabel", fallbackName);
      }

      toast.success("Connexion réussie !");
      onLoginSuccess(token);

      setTimeout(() => {
        const normalizedRole = role.toLowerCase();
        if (normalizedRole === "admin") navigate("/admin/dashboard/accueil");
        else if (normalizedRole === "doctor") navigate("/doctor/dashboard");
        else if (normalizedRole === "patient") navigate("/dashboard");
        else navigate("/");
      }, 1000);
    } catch (error: unknown) {
      console.error("LoginPage: Login error:", error);
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const message =
          error.response?.data?.message || "Échec de la connexion";
        if (status === 401) {
          toast.error("Identifiants incorrects. Veuillez réessayer.");
        } else if (status === 403) {
          toast.error("Accès non autorisé. Contactez l'administrateur.");
        } else if (status === 400) {
          toast.error("Requête invalide. Vérifiez vos données.");
        } else {
          toast.error(message);
        }
      } else {
        toast.error("Une erreur inattendue est survenue.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getIdentifierIcon = () => {
    const value = formData.identifier.toLowerCase();
    if (value.includes("@")) return <Mail className="w-5 h-5" />;
    if (/^\d/.test(value)) return <Phone className="w-5 h-5" />;
    return <User className="w-5 h-5" />;
  };

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />
      <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-teal-50">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-80 h-80 bg-blue-100/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-teal-100/30 rounded-full blur-3xl pulse-animation"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-green-50/20 rounded-full blur-3xl pulse-animation-delayed"></div>
        </div>

        <div className="absolute inset-0 overflow-hidden">
          {[
            {
              Icon: Heart,
              color: "text-red-200",
              className: "float-animation",
            },
            {
              Icon: Stethoscope,
              color: "text-blue-200",
              className: "float-animation delay-500",
            },
            {
              Icon: Activity,
              color: "text-green-200",
              className: "float-animation delay-1000",
            },
            {
              Icon: UserCheck,
              color: "text-teal-200",
              className: "float-animation delay-1500",
            },
            {
              Icon: Calendar,
              color: "text-indigo-200",
              className: "float-animation delay-2000",
            },
            {
              Icon: FileText,
              color: "text-slate-200",
              className: "float-animation delay-2500",
            },
          ].map(({ Icon, color, className }, i) => (
            <div
              key={i}
              className={`absolute ${color} opacity-40 ${className}`}
              style={{
                left: `${10 + i * 15}%`,
                top: `${10 + i * 12}%`,
              }}
            >
              <Icon className="w-6 h-6" />
            </div>
          ))}
        </div>

        <div className="relative z-10 min-h-screen flex items-center justify-center p-4 sm:p-6">
          <div className="w-full max-w-md">
            <div className="text-center mb-8 fade-in-animation">
              <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-blue-500 to-teal-500 rounded-full mb-6 shadow-xl">
                <div className="flex items-center space-x-1">
                  <Stethoscope className="w-8 h-8 text-white" />
                  <Heart className="w-6 h-6 text-white animate-pulse" />
                </div>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">
                <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                  MIMAP
                </span>
              </h1>
              <p className="text-slate-600 text-lg mb-4">
                Espace Professionnel de Santé
              </p>
              <div className="flex items-center justify-center space-x-6 text-sm text-slate-500">
                <div className="flex items-center space-x-2">
                  <UserCheck className="w-4 h-4 text-blue-500" />
                  <span>Patients</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Stethoscope className="w-4 h-4 text-teal-500" />
                  <span>Médecins</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4 text-green-500" />
                  <span>Admin</span>
                </div>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 sm:p-8 shadow-xl border border-blue-100 slide-up-animation">
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl sm:text-2xl font-semibold text-slate-700 mb-1">
                    Connexion Sécurisée
                  </h2>
                  <p className="text-slate-500 text-sm">
                    Accédez à votre espace professionnel
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Identifiant Professionnel
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors duration-300">
                      {getIdentifierIcon()}
                    </div>
                    <input
                      name="identifier"
                      value={formData.identifier}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("identifier")}
                      onBlur={() => setFocusedField("")}
                      type="text"
                      placeholder="Email, ID médecin ou téléphone"
                      className="w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-700 placeholder-slate-400 focus:border-blue-400 focus:bg-white outline-none transition-all duration-300"
                      required
                    />
                    <div
                      className={`absolute inset-0 rounded-xl bg-gradient-to-r from-blue-400/10 to-teal-400/10 pointer-events-none transition-opacity duration-300 ${focusedField === "identifier"
                          ? "opacity-100"
                          : "opacity-0"
                        }`}
                    ></div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Mot de passe sécurisé
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors duration-300">
                      <Lock className="w-5 h-5" />
                    </div>
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("password")}
                      onBlur={() => setFocusedField("")}
                      placeholder="Votre mot de passe professionnel"
                      className="w-full pl-12 pr-12 py-4 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-700 placeholder-slate-400 focus:border-blue-400 focus:bg-white outline-none transition-all duration-300"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-blue-500 transition-colors duration-300"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                    <div
                      className={`absolute inset-0 rounded-xl bg-gradient-to-r from-blue-400/10 to-teal-400/10 pointer-events-none transition-opacity duration-300 ${focusedField === "password"
                          ? "opacity-100"
                          : "opacity-0"
                        }`}
                    ></div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="w-full relative overflow-hidden bg-gradient-to-r from-blue-500 to-teal-500 text-white py-4 rounded-xl font-semibold hover:from-blue-600 hover:to-teal-600 focus:ring-2 focus:ring-blue-400/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] group shadow-lg"
                >
                  <div className="flex items-center justify-center space-x-2">
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Vérification en cours...</span>
                      </>
                    ) : (
                      <>
                        <Shield className="w-5 h-5" />
                        <span>Accéder à l'espace médical</span>
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                      </>
                    )}
                  </div>
                  <div className="absolute inset-0 bg-white/20 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"></div>
                </button>

                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center space-x-2 text-slate-600 cursor-pointer hover:text-slate-800 transition-colors duration-300">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 bg-slate-50 text-blue-500 focus:ring-blue-400/50"
                    />
                    <span>Rester connecté (session sécurisée)</span>
                  </label>
                  <a
                    href="#"
                    className="text-blue-500 hover:text-blue-600 transition-colors duration-300 font-medium"
                  >
                    Aide connexion
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
